import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Search, ShieldCheck, Sparkles, BellRing } from "lucide-react";

import { StudyCard, StudyCardSkeleton } from "../components/StudyCard";
import StudyDetailsModal from "../components/StudyDetailsModal";
import StickyRegisterCTA from "../components/StickyRegisterCTA";
import FollowStrip from "../components/FollowStrip";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /research/:slug , an indexable, evergreen SEO landing page per medical
 * condition. The data (canonical condition label, open + closed studies,
 * templated FAQ) all comes from a single API call.
 *
 * The page is deliberately content-rich (intro + FAQ + study lists) so
 * Google has enough to index it as a useful page, not just a
 * thin-content listing.
 */
export default function ResearchConditionPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState(null);
  const [selectedStudy, setSelectedStudy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setData(null);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/research/${slug}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) {
          if (err?.response?.status === 404) setNotFound(true);
          else setNotFound(true); // any error -> 404-style screen
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Build the JSON-LD payload only when we have data , combines a
  // MedicalCondition node (helps Google render rich panels for
  // condition-name searches) with a FAQPage node (so the FAQ block
  // becomes eligible for FAQ-rich-snippets in search results).
  const jsonLd = data
    ? [
        {
          "@context": "https://schema.org",
          "@type": "MedicalCondition",
          name: data.condition,
          url: `https://www.meditalk.world/research/${data.slug}`,
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: data.faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.meditalk.world/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Research",
              item: "https://www.meditalk.world/research",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: data.condition,
              item: `https://www.meditalk.world/research/${data.slug}`,
            },
          ],
        },
      ]
    : null;

  usePageMeta({
    path: `/research/${slug}`,
    title: data
      ? `Paid ${data.display.toLowerCase()} research studies | MediTalk`
      : "MediTalk research",
    description: data
      ? `Take part in paid healthcare research about ${data.display.toLowerCase()}. ${data.open_studies.length > 0 ? `${data.open_studies.length} open ${data.open_studies.length === 1 ? "study" : "studies"} right now.` : "Register your interest , we'll email you when one opens."} Interviews, focus groups and surveys , compensated for your time.`
      : "Loading…",
    jsonLd,
  });

  // ------- Render --------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen">
        <section className="bg-[var(--dark-bg)] py-16">
          <div className="container-main">
            <div className="h-8 w-32 rounded bg-white/10 mb-4 animate-pulse" />
            <div className="h-10 w-2/3 rounded bg-white/10 mb-3 animate-pulse" />
            <div className="h-5 w-1/2 rounded bg-white/10 animate-pulse" />
          </div>
        </section>
        <section className="py-12 container-main grid grid-cols-1 md:grid-cols-2 gap-6">
          <StudyCardSkeleton />
          <StudyCardSkeleton />
        </section>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen">
        <section className="bg-[var(--dark-bg)] py-20">
          <div className="container-main text-center">
            <span className="badge badge-sage mb-4">Not found</span>
            <h1 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-white mb-3">
              We don't have a page for that condition yet
            </h1>
            <p className="text-[var(--gray-light)] mb-8 max-w-xl mx-auto">
              You can still register your interest , we'll match you to
              studies as soon as one opens for your condition.
            </p>
            <Link
              to="/register"
              className="cta-pulse-lime group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-extrabold text-base hover:bg-[var(--lime-dark)] transition-colors"
              data-testid="research-notfound-register"
            >
              Register interest
              <ArrowRight size={18} strokeWidth={2.75} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const display = data.display;
  const lowDisplay = display.toLowerCase();
  const hasOpen = data.open_studies.length > 0;

  return (
    <div className="min-h-screen" data-testid="research-condition-page">
      {/* HERO , DARK ------------------------------------------------- */}
      <section className="relative bg-[var(--dark-bg)] py-10 md:py-16 overflow-hidden">
        {/* soft lime glow orb behind the CTA so the eye is drawn to it */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl"
        />
        <div className="container-main relative">
          <nav
            className="flex items-center gap-2 text-xs text-[var(--gray-light)] mb-4"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span aria-hidden="true">›</span>
            <Link to="/research" className="hover:text-white">Research</Link>
            <span aria-hidden="true">›</span>
            <span className="text-[var(--lime)]">{data.condition}</span>
          </nav>
          <span className="badge badge-terracotta mb-3">Paid research</span>
          <h1
            className="text-3xl md:text-5xl font-['Outfit'] font-bold tracking-tight text-white mb-3 max-w-3xl"
            data-testid="research-h1"
          >
            Paid {lowDisplay} research studies
          </h1>
          <p className="text-[var(--gray-light)] max-w-2xl text-base md:text-lg leading-relaxed mb-6">
            {hasOpen
              ? `${data.open_studies.length} open ${data.open_studies.length === 1 ? "study" : "studies"} right now for people living with ${lowDisplay} , and we pay for your time.`
              : `Be first in line for our next ${lowDisplay} study , no open spots today, but we launch new ones every few weeks.`}
          </p>

          {/* Primary register CTA , huge, glowing, always above the fold.
              Lives directly under the H1 so even a visitor that bounces
              within 3 seconds sees the action. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 mb-6">
            <Link
              to="/register"
              className="cta-pulse-lime group inline-flex items-center justify-center gap-2.5 px-8 py-4 md:px-9 md:py-5 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-extrabold text-base md:text-lg hover:bg-[var(--lime-dark)] transition-colors w-full sm:w-auto"
              data-testid="research-hero-register"
            >
              Register your interest
              <ArrowRight
                size={20}
                strokeWidth={2.75}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <div className="text-sm text-white/75 leading-snug">
              <div className="font-semibold text-white">Two minutes. No spam.</div>
              <div>{hasOpen
                ? "Or scroll down to see this week's open studies."
                : `We'll email you the moment an ${lowDisplay} study opens.`}</div>
            </div>
          </div>

          {hasOpen && (
            <a
              href="#open-studies"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--lime)] hover:text-white mb-6"
              data-testid="research-hero-see-studies"
            >
              ↓ See {data.open_studies.length}{" "}
              open {data.open_studies.length === 1 ? "study" : "studies"}
            </a>
          )}

          <div className="flex flex-wrap items-center gap-2 text-xs text-white/70">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
              <ShieldCheck size={12} className="text-[var(--lime)]" />
              GDPR safe
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
              <Sparkles size={12} className="text-[var(--lime)]" />
              Compensated participation
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
              <Search size={12} className="text-[var(--lime)]" />
              No long applications
            </span>
          </div>
        </div>
      </section>

      {/* OPEN STUDIES , LIGHT --------------------------------------- */}
      <section
        id="open-studies"
        className="py-12 md:py-16 bg-white scroll-mt-8"
      >
        <div className="container-main">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-[var(--navy)]">
                {hasOpen
                  ? `Open ${lowDisplay} studies right now`
                  : `No open ${lowDisplay} studies right now`}
              </h2>
              <p className="text-sm text-[var(--text-muted)] mt-1">
                {hasOpen
                  ? "Each card shows the format, time commitment and compensation. Tap a card to see the full brief and register."
                  : "Register your interest below , we'll email you as soon as one opens."}
              </p>
            </div>
          </div>

          {hasOpen ? (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="research-open-grid"
            >
              {data.open_studies.map((s) => (
                <StudyCard
                  key={s.id}
                  study={s}
                  variant="light"
                  onRegister={(study) => setSelectedStudy(study)}
                />
              ))}
            </div>
          ) : (
            <div
              className="relative overflow-hidden rounded-2xl border-2 border-[var(--lime)]/40 bg-gradient-to-br from-[var(--lime)]/10 via-white to-white p-7 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
              data-testid="research-empty-state-card"
            >
              {/* lime corner accent */}
              <div
                aria-hidden="true"
                className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[var(--lime)]/20 blur-2xl"
              />
              <div className="relative flex items-start gap-4">
                <div className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--lime)] text-[var(--dark-bg)]">
                  <BellRing size={22} strokeWidth={2.4} />
                </div>
                <div>
                  <p className="text-lg md:text-xl text-[var(--charcoal)] font-bold mb-1">
                    Be first in line , it's two minutes.
                  </p>
                  <p className="text-sm text-[var(--text-muted)] max-w-xl leading-relaxed">
                    We typically launch new {lowDisplay} studies every few
                    weeks. Registered participants hear about them before
                    they go live on the public site.
                  </p>
                </div>
              </div>
              <Link
                to="/register"
                className="cta-pulse-lime group relative inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-extrabold text-base hover:bg-[var(--lime-dark)] transition-colors flex-shrink-0 w-full md:w-auto"
                data-testid="research-register-cta"
              >
                Register interest
                <ArrowRight
                  size={18}
                  strokeWidth={2.75}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
            </div>
          )}

          {/* Empty-state-only follow strip , the visitor came looking
              for a study that doesn't exist today, and they may not be
              ready to register. Offer a softer touchpoint. */}
          {!hasOpen && (
            <div className="mt-6">
              <FollowStrip
                variant="light"
                source="research-empty"
                campaign={`research-${slug}`}
                medium="empty-state"
                title={`Or follow us , we post new ${lowDisplay} studies on socials too`}
                subtitle="Sometimes studies open between newsletter sends. Following us is the fastest way to hear about them first."
                testId="research-empty-follow-strip"
              />
            </div>
          )}

          {/* Inline mini-nudge , only shown when there ARE open studies,
              because the empty-state branch above already has its own
              prominent register CTA. Keeps the register option visible
              even after a visitor scans the open-studies grid. */}
          {hasOpen && (
            <div
              className="mt-8 rounded-xl border border-[var(--light-border)] bg-[var(--bone-soft)] px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              data-testid="research-mid-strip"
            >
              <p className="text-sm text-[var(--charcoal)]">
                <span className="font-semibold">Not a fit for these?</span>{" "}
                <span className="text-[var(--text-muted)]">
                  Register interest and we'll match you to the next {lowDisplay} study.
                </span>
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--charcoal)] text-white text-sm font-semibold hover:bg-[var(--dark-bg)] transition-colors flex-shrink-0"
                data-testid="research-mid-register"
              >
                Register interest
                <ArrowRight size={14} strokeWidth={2.5} />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CLOSED STUDIES AS SOCIAL PROOF , DARK ---------------------- */}
      {data.closed_studies.length > 0 && (
        <section className="py-12 md:py-16 bg-[var(--dark-bg)]">
          <div className="container-main">
            <h2 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-white mb-2">
              Recent {lowDisplay} studies we've run
            </h2>
            <p className="text-sm text-[var(--gray-light)] mb-8 max-w-2xl">
              A snapshot of what {lowDisplay} research on MediTalk actually
              looks like. These studies have closed , but they're a good
              indication of the format and time-commitment for our next one.
            </p>
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="research-closed-grid"
            >
              {data.closed_studies.map((s) => (
                <StudyCard
                  key={s.id}
                  study={s}
                  variant="dark"
                  onRegister={(study) => setSelectedStudy(study)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ , LIGHT ------------------------------------------------ */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container-main max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-[var(--navy)] mb-8">
            {display} research , frequently asked questions
          </h2>
          <div className="space-y-3" data-testid="research-faqs">
            {data.faqs.map((f, i) => (
              <details
                key={i}
                className="rounded-2xl border border-[var(--light-border)] bg-white p-5 md:p-6 group [&[open]]:border-[var(--teal)]"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-base md:text-lg font-semibold text-[var(--charcoal)] [&::-webkit-details-marker]:hidden">
                  {f.question}
                  <span
                    className="text-[var(--text-muted)] text-2xl leading-none transition-transform group-[&[open]]:rotate-45"
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <p className="text-sm md:text-base text-[var(--text-muted)] leading-relaxed mt-3">
                  {f.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA , DARK ---------------------------------------- */}
      <section className="py-14 md:py-20 bg-[var(--dark-bg)]">
        <div className="container-main text-center max-w-2xl">
          <h2 className="text-2xl md:text-4xl font-['Outfit'] font-bold text-white mb-4">
            Ready to share your {lowDisplay} experience?
          </h2>
          <p className="text-[var(--gray-light)] mb-8 leading-relaxed">
            Two minutes to register. We'll only contact you about studies
            that match what you've told us , no spam, no third-party
            sharing.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/register"
              className="cta-pulse-lime group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-extrabold text-base hover:bg-[var(--lime-dark)] transition-colors"
              data-testid="research-final-register"
            >
              Register interest
              <ArrowRight
                size={18}
                strokeWidth={2.75}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <Link
              to="/patient/studies"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors"
              data-testid="research-browse-all"
            >
              Browse all open studies
            </Link>
          </div>
        </div>
      </section>

      {/* Mobile-only sticky bottom register bar , appears after the user
          scrolls past the hero CTA so the action stays one tap away. */}
      <StickyRegisterCTA />

      {selectedStudy && (
        <StudyDetailsModal
          study={selectedStudy}
          audience={selectedStudy.study_type === "caregiver" ? "caregiver" : "patient"}
          onClose={() => setSelectedStudy(null)}
        />
      )}
    </div>
  );
}
